import { useCallback, useMemo, useState, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Copy, GitCommitHorizontal, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  apiClient,
  type SystemBuildInfoDto,
  type SystemBuildInfoInstanceDto,
  type SystemBuildInfoServiceDto,
} from '@/api/client';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FetchErrorCallout } from '@/components/ui/fetch-error-callout';
import { PageHeader } from '@/components/shell/PageHeader';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

const BUILD_INFO_QUERY_KEY = ['system', 'build-info'] as const;
const STALE_TIME_MS = 30_000;

type StatusTone = 'up' | 'down' | 'starting' | 'unknown';

function statusTone(status: string | null | undefined): StatusTone {
  if (status === 'UP') return 'up';
  if (status === 'DOWN' || status === 'OUT_OF_SERVICE') return 'down';
  if (status === 'STARTING') return 'starting';
  return 'unknown';
}

const STATUS_BADGE_CLASSES: Record<StatusTone, string> = {
  up: 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100',
  down: 'bg-red-100 text-red-800 hover:bg-red-100',
  starting: 'bg-amber-100 text-amber-800 hover:bg-amber-100',
  unknown: 'bg-muted text-muted-foreground hover:bg-muted',
};

function formatRelative(iso: string | null | undefined): string | null {
  if (iso === null || iso === undefined || iso === '' || iso === 'unknown') return null;
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  const deltaSec = Math.floor((Date.now() - ms) / 1000);
  if (deltaSec < 60) return `${String(deltaSec)}s ago`;
  if (deltaSec < 3600) return `${String(Math.floor(deltaSec / 60))}m ago`;
  if (deltaSec < 86_400) return `${String(Math.floor(deltaSec / 3600))}h ago`;
  return `${String(Math.floor(deltaSec / 86_400))}d ago`;
}

function isMeaningful(value: string | null | undefined): value is string {
  return value !== null && value !== undefined && value !== '' && value !== 'unknown';
}

function dominantCommit(instances: readonly SystemBuildInfoInstanceDto[]): string | null {
  const counts = new Map<string, number>();
  for (const i of instances) {
    const c = i.git.commit_id_abbrev;
    if (!isMeaningful(c)) continue;
    counts.set(c, (counts.get(c) ?? 0) + 1);
  }
  let dominant: string | null = null;
  let max = 0;
  for (const [c, n] of counts) {
    if (n > max) {
      dominant = c;
      max = n;
    }
  }
  return dominant;
}

function hasDrift(instances: readonly SystemBuildInfoInstanceDto[]): boolean {
  const seen = new Set<string>();
  for (const i of instances) {
    const c = i.git.commit_id_abbrev;
    if (isMeaningful(c)) seen.add(c);
  }
  return seen.size > 1;
}

export function AdminBuildInfoPage(): ReactElement {
  const { t } = useTranslation();
  const query = useQuery({
    queryKey: BUILD_INFO_QUERY_KEY,
    queryFn: ({ signal }) => apiClient.getSystemBuildInfo({ signal }),
    staleTime: STALE_TIME_MS,
  });

  const data: SystemBuildInfoDto | undefined = query.data;

  const driftingServices = useMemo<readonly string[]>(() => {
    if (data === undefined) return [];
    return data.services.filter((s) => hasDrift(s.instances)).map((s) => s.service_id);
  }, [data]);

  const handleRefresh = useCallback(() => {
    query.refetch().catch(() => undefined);
  }, [query]);

  const aggregatedAgo = data !== undefined ? formatRelative(data.aggregated_at) : null;

  return (
    <>
      <PageHeader
        icon={<GitCommitHorizontal className="h-[18px] w-[18px]" aria-hidden="true" />}
        title={t('routes.admin.buildInfo.heading')}
        subtitle={
          aggregatedAgo !== null
            ? t('routes.admin.buildInfo.aggregatedAt', { ago: aggregatedAgo })
            : t('routes.admin.buildInfo.subheading')
        }
        actions={
          <Button size="sm" variant="outline" onClick={handleRefresh} disabled={query.isFetching}>
            <RefreshCw
              className={cn('h-4 w-4', query.isFetching && 'animate-spin')}
              aria-hidden="true"
            />
            {t('routes.admin.buildInfo.refresh')}
          </Button>
        }
      />
      <div className="px-8 py-5 space-y-5 bg-background">
        {driftingServices.length > 0 && <DriftBanner services={driftingServices} />}

        <FrontendCard />

        {query.isPending && <BackendSkeleton />}

        {query.isError && (
          <div className="max-w-xl">
            <FetchErrorCallout
              error={query.error}
              heading={t('routes.admin.buildInfo.error.heading')}
              message={t('routes.admin.buildInfo.error.message')}
              codeLabel={t('routes.admin.users.error.codeLabel')}
              onRetry={handleRefresh}
              retryLabel={t('routes.admin.buildInfo.error.retry')}
            />
          </div>
        )}

        {query.isSuccess && (
          <BackendSection
            data={query.data}
            onCopy={() => {
              copyJson(query.data);
            }}
          />
        )}
      </div>
    </>
  );
}

function DriftBanner({ services }: { readonly services: readonly string[] }): ReactElement {
  const { t } = useTranslation();
  const list = services.join(', ');
  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <span>
        {t('routes.admin.buildInfo.drift.banner', {
          count: services.length,
          services: list,
        })}
      </span>
    </div>
  );
}

function FrontendCard(): ReactElement {
  const { t } = useTranslation();
  const builtAgo = formatRelative(__APP_BUILD_TIME__);
  return (
    <Card className="border-brand-soft">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">
          {t('routes.admin.buildInfo.frontendCard.title')}
        </CardTitle>
        <CardDescription>
          {t('routes.admin.buildInfo.frontendCard.version', { version: __APP_VERSION__ })}
          {' · '}
          <CommitChip value={__APP_GIT_COMMIT__} />
          {builtAgo !== null && (
            <>
              {' · '}
              {t('routes.admin.buildInfo.frontendCard.builtAgo', { ago: builtAgo })}
            </>
          )}
        </CardDescription>
      </CardHeader>
    </Card>
  );
}

interface BackendSectionProps {
  readonly data: SystemBuildInfoDto;
  readonly onCopy: () => void;
}

function BackendSection({ data, onCopy }: BackendSectionProps): ReactElement {
  const { t } = useTranslation();
  if (data.services.length === 0) {
    return (
      <p className="text-sm text-muted-foreground" role="status">
        {t('routes.admin.buildInfo.empty')}
      </p>
    );
  }
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {t('routes.admin.buildInfo.backendHeading')}
        </h2>
        <CopyJsonButton onClick={onCopy} />
      </div>
      <div className="space-y-3">
        {data.services.map((service) => (
          <ServiceCard key={service.service_id} service={service} />
        ))}
      </div>
      <p className="text-xs text-muted-foreground pt-1">
        {t('routes.admin.buildInfo.clusterInitFootnote')}
      </p>
    </div>
  );
}

function ServiceCard({ service }: { readonly service: SystemBuildInfoServiceDto }): ReactElement {
  const { t } = useTranslation();
  const [open, setOpen] = useState<boolean>(true);
  const drift = hasDrift(service.instances);
  const dominant = dominantCommit(service.instances);
  const summary = service.instances[0];
  return (
    <Card className={cn(drift && 'border-amber-300')}>
      <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0 pb-3">
        <div className="min-w-0 flex-1">
          <CardTitle className="text-base font-semibold tracking-tight">
            {service.service_id.toLowerCase()}
          </CardTitle>
          <CardDescription className="mt-1 text-xs">
            {summary !== undefined && isMeaningful(summary.build.version) && (
              <>
                {t('routes.admin.buildInfo.frontendCard.version', {
                  version: summary.build.version,
                })}
                {' · '}
              </>
            )}
            {dominant !== null && <CommitChip value={dominant} />}
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="font-normal">
            {t('routes.admin.buildInfo.instanceCount', { count: service.instances.length })}
          </Badge>
          {drift && (
            <Badge className="bg-amber-200 text-amber-900 hover:bg-amber-200">
              {t('routes.admin.buildInfo.drift.badge')}
            </Badge>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setOpen((prev) => !prev);
            }}
            aria-expanded={open}
          >
            {open ? '▾' : '▸'}
          </Button>
        </div>
      </CardHeader>
      {open && (
        <CardContent className="pt-0">
          <InstanceTable instances={service.instances} />
        </CardContent>
      )}
    </Card>
  );
}

function InstanceTable({
  instances,
}: {
  readonly instances: readonly SystemBuildInfoInstanceDto[];
}): ReactElement {
  const { t } = useTranslation();
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-border text-left text-muted-foreground">
            <th className="py-2 pr-3 font-medium">{t('routes.admin.buildInfo.table.instance')}</th>
            <th className="py-2 pr-3 font-medium">{t('routes.admin.buildInfo.table.status')}</th>
            <th className="py-2 pr-3 font-medium">{t('routes.admin.buildInfo.table.version')}</th>
            <th className="py-2 pr-3 font-medium">{t('routes.admin.buildInfo.table.buildTime')}</th>
            <th className="py-2 pr-3 font-medium">{t('routes.admin.buildInfo.table.commit')}</th>
            <th className="py-2 pr-3 font-medium">{t('routes.admin.buildInfo.table.branch')}</th>
            <th className="py-2 pr-3 font-medium">{t('routes.admin.buildInfo.table.host')}</th>
          </tr>
        </thead>
        <tbody>
          {instances.map((i) => {
            const tone = statusTone(i.status);
            const builtAgo = formatRelative(i.build.time);
            return (
              <tr key={i.instance_id ?? `${i.host ?? '?'}:${String(i.port)}`} className="border-b border-border last:border-0">
                <td className="py-2 pr-3 font-mono text-[11px]">{i.instance_id ?? em()}</td>
                <td className="py-2 pr-3">
                  <Badge className={cn('font-normal', STATUS_BADGE_CLASSES[tone])}>
                    {i.status ?? '—'}
                  </Badge>
                </td>
                <td className="py-2 pr-3">{i.build.version ?? em()}</td>
                <td className="py-2 pr-3" title={i.build.time ?? undefined}>
                  {builtAgo ?? em()}
                </td>
                <td className="py-2 pr-3">
                  <CommitChip value={i.git.commit_id_abbrev} fullValue={i.git.commit_id} />
                </td>
                <td className="py-2 pr-3">{i.git.branch ?? em()}</td>
                <td className="py-2 pr-3 text-muted-foreground">
                  {i.host ?? em()}
                  {i.host !== null && `:${String(i.port)}`}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function em(): string {
  return '—';
}

interface CommitChipProps {
  readonly value: string | null | undefined;
  readonly fullValue?: string | null;
}

function CommitChip({ value, fullValue }: CommitChipProps): ReactElement {
  if (!isMeaningful(value)) return <span className="text-muted-foreground">—</span>;
  const onClick = (): void => {
    void navigator.clipboard.writeText(fullValue ?? value).catch(() => undefined);
  };
  return (
    <button
      type="button"
      onClick={onClick}
      title={fullValue ?? value}
      className="font-mono text-[11px] rounded bg-muted px-1.5 py-0.5 hover:bg-accent"
    >
      {value}
    </button>
  );
}

function CopyJsonButton({ onClick }: { readonly onClick: () => void }): ReactElement {
  const { t } = useTranslation();
  const [copied, setCopied] = useState<boolean>(false);
  const handle = useCallback(() => {
    onClick();
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 1500);
  }, [onClick]);
  return (
    <Button size="sm" variant="outline" onClick={handle}>
      <Copy className="h-3.5 w-3.5" aria-hidden="true" />
      {copied ? t('routes.admin.buildInfo.copied') : t('routes.admin.buildInfo.copyJson')}
    </Button>
  );
}

function copyJson(data: SystemBuildInfoDto): void {
  void navigator.clipboard.writeText(JSON.stringify(data, null, 2)).catch(() => undefined);
}

function BackendSkeleton(): ReactElement {
  return (
    <div className="space-y-3">
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-24 w-full" />
    </div>
  );
}
